'use client';

import { type Permission, PermissionCategory, PermissionType } from '@repo/api-gw-sdk';
import { useMemo, type PropsWithChildren } from 'react';
import { rolesComparer } from '@/configs/roles';
import { RolesContext } from '@/contexts/rolesContext';
import { isStorageGatewayPermission, Permissions, type PermissionDescriptor } from './permissions';
import { buildRolesMap } from './roleLookup';
import { isScopeAuthorized } from './scopeAuthorization';
import { useFeatureFlags } from './useFeatureFlags';
import { useNonProjectDal } from './useNonProjectDal';
import { useUser } from './useUser';
export const RolesProvider = ({
  children
}: PropsWithChildren) => {
  const {
    nonProjectDal: dal
  } = useNonProjectDal();
  const {
    ZeroEtlEnabled,
    dlsgFrontend
  } = useFeatureFlags();
  const {
    user
  } = useUser();
  const {
    data: rolesBody,
    mutate: reloadRoles
  } = dal.roles.useList();
  const {
    data: permissionsBody
  } = dal.roles.useListPermissions();
  const roles = useMemo(() => (rolesBody?.roles ?? []).sort(rolesComparer), [rolesBody]);
  const rolesMap = useMemo(() => buildRolesMap(roles, user?.selectedRole), [roles, user?.selectedRole]);
  const permissions = useMemo(() => (permissionsBody?.permissions ?? []).reduce<Record<string, PermissionDescriptor>>((acc: Record<string, PermissionDescriptor>, permission: Permission) => {
    if (!ZeroEtlEnabled && permission.permissionType === PermissionType.ZeroEtlPoliciesManage) {
      return acc;
    }

    // Only two of the gateway's permissions are flag-gated server-side; the rest are
    // returned to every account. Gating them in the catalog would start rejecting role
    // writes for anyone who already granted them, so the drop happens here instead.
    if (!dlsgFrontend && isStorageGatewayPermission(permission.permissionType)) {
      return acc;
    }
    if (Permissions[permission.permissionType]) {
      acc[permission.permissionType] = {
        ...Permissions[permission.permissionType],
        ...permission
      };
    }
    return acc;
  }, {}), [ZeroEtlEnabled, dlsgFrontend, permissionsBody]);
  return <RolesContext.Provider value={{
    roles,
    rolesMap,
    permissions,
    isGlobalRole: role => !!role?.permissionGrants.some(grant => permissions[grant.permission]?.category === PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT),
    isAuthorized: (scope, roleOverride) => isScopeAuthorized(scope, roleOverride?.scopes || user?.selectedRole.scopes || []),
    isAuthorizedToGroup: (group, roleOverride_0) => {
      const groupPermissionTypes = Object.values(permissions).filter(permission_0 => permission_0.group === group).map(permission_1 => permission_1.permissionType);
      const grants = roleOverride_0?.permissionGrants || user?.selectedRole.permissionGrants || [];
      return grants.some(grant_0 => groupPermissionTypes.includes(grant_0.permission));
    },
    isAuthorizedResource: (scope_0, resource) => {
      if (!resource) {
        return false;
      }
      if (!resource.metadata?.disallowedScopes) {
        return isScopeAuthorized(scope_0, user?.selectedRole.scopes || []);
      }
      if (resource.metadata.disallowedScopes.includes(scope_0)) {
        return false;
      }
      return isScopeAuthorized(scope_0, user?.selectedRole.scopes || []);
    },
    refreshRoles: async () => {
      await reloadRoles();
    }
  }}>
      {children}
    </RolesContext.Provider>;
};